import {
  BET_SLIP_FORMAT,
  BET_SLIP_READY_TIMEOUT_MS,
  BET_SLIP_TAG_TIMEOUT_MS,
  type BetSlipFormat,
  isBetSlipEnabled,
} from './betSlipConfig';
import {
  computeTag,
  prepareMaterial,
  serializeReference,
} from './betSlipCrypto';
import { readRecord, writeRecord } from './betSlipStorage';

export interface BetSlipMetadata {
  betSlipRef: string;
  betSlipTag: string;
  betSlipFormat: BetSlipFormat;
}

interface CacheEntry {
  primary: CryptoKey;
  referenceText: string;
}

let cache: CacheEntry | null = null;
let readyPromise: Promise<void> | null = null;

const initialize = async (): Promise<void> => {
  if (!isBetSlipEnabled()) return;
  if (typeof indexedDB === 'undefined') return;
  if (typeof crypto === 'undefined' || !crypto.subtle) return;

  try {
    const existing = await readRecord();
    if (existing) {
      cache = {
        primary: existing.primary,
        referenceText: existing.referenceText,
      };
      return;
    }
    const material = await prepareMaterial();
    const referenceText = await serializeReference(material.reference);
    await writeRecord({
      primary: material.primary,
      referenceText,
      createdAt: Date.now(),
    });
    cache = { primary: material.primary, referenceText };
  } catch {
    cache = null;
  }
};

export const ensureReady = (): Promise<void> => {
  if (!readyPromise) {
    readyPromise = initialize();
  }
  return readyPromise;
};

const waitForCache = async (): Promise<CacheEntry | null> => {
  if (cache) return cache;
  await Promise.race([
    ensureReady().catch(() => {}),
    new Promise<void>((resolve) => {
      setTimeout(resolve, BET_SLIP_READY_TIMEOUT_MS);
    }),
  ]);
  return cache;
};

const tagWithTimeout = (
  entry: CacheEntry,
  clientBetId: string
): Promise<BetSlipMetadata | null> =>
  new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, BET_SLIP_TAG_TIMEOUT_MS);
    computeTag(entry.primary, clientBetId)
      .then((tag) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({
          betSlipRef: entry.referenceText,
          betSlipTag: tag,
          betSlipFormat: BET_SLIP_FORMAT,
        });
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(null);
      });
  });

export const signClientBetId = async (
  clientBetId: string
): Promise<BetSlipMetadata | null> => {
  if (!isBetSlipEnabled()) return null;
  if (!clientBetId) return null;
  const entry = await waitForCache();
  if (!entry) return null;
  return tagWithTimeout(entry, clientBetId);
};

export const attachBetSlipMetadata = async <T extends { clientBetId: string }>(
  bet: T
): Promise<T & Partial<BetSlipMetadata>> => {
  const metadata = await signClientBetId(bet.clientBetId);
  return metadata ? { ...bet, ...metadata } : bet;
};
